import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { criarUsuario, type Papel } from '@/services/api/domain-access';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { RefreshCw, X, UserPlus } from 'lucide-react';
import { cn } from '@/lib/utils';

const PAPEIS: { valor: Papel; titulo: string; descricao: string }[] = [
    {
        valor: 'user',
        titulo: 'Usuário',
        descricao: 'Sem domínios atribuídos. Enxerga tudo até virar gestor.',
    },
    {
        valor: 'manager',
        titulo: 'Gestor',
        descricao: 'Enxerga apenas os domínios que você liberar para ele.',
    },
    {
        valor: 'admin',
        titulo: 'Administrador',
        descricao: 'Acesso total, incluindo esta tela. Use com parcimônia.',
    },
];

interface Props {
    aberto: boolean;
    aoFechar: () => void;
    aoCriar?: (userId: number) => void;
}

export default function NovoUsuarioModal({ aberto, aoFechar, aoCriar }: Props) {
    const [nome, setNome] = useState('');
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [papel, setPapel] = useState<Papel>('manager');

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const limpar = () => {
        setNome(''); setEmail(''); setSenha(''); setPapel('manager');
    };

    const criar = useMutation({
        mutationFn: () => criarUsuario({ name: nome, email, password: senha, role: papel }),
        onSuccess: (res) => {
            toast({
                title: 'Usuário criado',
                description:
                    papel === 'manager'
                        ? `${nome} foi criado como gestor. Agora escolha os domínios dele.`
                        : `${nome} foi criado como ${papel}.`,
            });
            queryClient.invalidateQueries({ queryKey: ['users'] });
            limpar();
            aoFechar();
            if (res?.user?.id) aoCriar?.(res.user.id);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar usuário',
                description:
                    error.response?.data?.detail ||
                    error.response?.data?.errors?.[0]?.message ||
                    'Falha ao criar o usuário.',
            });
        },
    });

    if (!aberto) return null;

    const senhaCurta = senha.length > 0 && senha.length < 6;
    const podeSalvar = nome.trim().length >= 2 && email.includes('@') && senha.length >= 6;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={aoFechar} />

            <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                        <UserPlus className="h-5 w-5 text-blue-600" />
                        <h2 className="font-bold text-gray-900">Novo usuário</h2>
                    </div>
                    <button onClick={aoFechar} className="text-gray-400 hover:text-gray-600">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                            Nome
                        </label>
                        <input
                            value={nome}
                            onChange={(e) => setNome(e.target.value)}
                            placeholder="Maria Silva"
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm outline-none focus:ring-1 focus:ring-blue-200"
                            autoFocus
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                            E-mail
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="maria@rocketgroup.io"
                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-100 rounded-xl text-sm outline-none focus:ring-1 focus:ring-blue-200"
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                            Senha provisória
                        </label>
                        <input
                            type="text"
                            value={senha}
                            onChange={(e) => setSenha(e.target.value)}
                            placeholder="mínimo 6 caracteres"
                            className={cn(
                                'w-full px-4 py-2.5 bg-gray-50 border rounded-xl text-sm outline-none focus:ring-1',
                                senhaCurta
                                    ? 'border-red-200 focus:ring-red-200'
                                    : 'border-gray-100 focus:ring-blue-200',
                            )}
                        />
                        {senhaCurta && (
                            <p className="mt-1 text-xs text-red-500">A senha precisa de 6 caracteres ou mais.</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                            Nível de acesso
                        </label>
                        <div className="space-y-2">
                            {PAPEIS.map((p) => (
                                <button
                                    key={p.valor}
                                    onClick={() => setPapel(p.valor)}
                                    className={cn(
                                        'w-full text-left px-4 py-3 rounded-xl border transition-all',
                                        papel === p.valor
                                            ? 'border-blue-300 bg-blue-50/60 ring-1 ring-blue-200'
                                            : 'border-gray-100 bg-gray-50 hover:bg-white hover:border-gray-200',
                                    )}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className={cn(
                                            'h-3.5 w-3.5 rounded-full border-2',
                                            papel === p.valor ? 'border-blue-600 bg-blue-600' : 'border-gray-300',
                                        )} />
                                        <span className="font-semibold text-sm text-gray-900">{p.titulo}</span>
                                    </div>
                                    <p className="mt-1 ml-5.5 text-xs text-gray-500">{p.descricao}</p>
                                </button>
                            ))}
                        </div>
                    </div>

                    {papel === 'admin' && (
                        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                            Administrador enxerga <strong>todos</strong> os domínios e pode alterar acessos
                            de qualquer pessoa, inclusive rebaixar outros admins.
                        </p>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-6 py-4 bg-gray-50/60 border-t border-gray-100">
                    <Button variant="ghost" onClick={aoFechar} className="rounded-lg">
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => criar.mutate()}
                        disabled={!podeSalvar || criar.isPending}
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold gap-2"
                    >
                        {criar.isPending
                            ? <RefreshCw className="h-4 w-4 animate-spin" />
                            : <UserPlus className="h-4 w-4" />}
                        Criar usuário
                    </Button>
                </div>
            </div>
        </div>
    );
}
